import React, { useState } from 'react';
import { IntlShape, FormattedMessage } from 'react-intl';

import {
  FlowProgressHeader,
  StepMatch,
} from 'views/components/FlowProgressHeader/FlowProgressHeader';
import { Pricing } from 'views/components/Pricing/PricingController';
import { FlowButtonBar } from 'views/components/FlowButtonBar/FlowButtonBar';
import { TextInput } from 'views/components/TextInput/TextInput';
import { DateInput } from 'views/components/DateInput/DateInput';
import { getErrorMessageFor } from 'tools/utilities/getErrorMessageFor';
import { IPayroll } from 'types';
import { SelectInput } from 'views/components/SelectInput/SelectInput';
import { CheckboxInput } from 'views/components/CheckboxInput/CheckboxInput';
import {
  OPTION_OTHER, BANK_ASB, DD_AUTHORITY_TERMS_AND_CONDITIONS_URL, BANK_ACCOUNT_NAME_MAX_LENGTH, OPTION_OTHER_PAYROLL_SOFTWARE,
} from 'store/modules/app/constants';
import { InfoBlock } from 'views/components/InfoBlock/InfoBlock';
import { scrollToError } from 'tools/utilities/scrollToError';
import { MaskedInput } from 'views/components/MaskedInput/MaskedInput';
import { isValidBankAccountNumber } from 'tools/utilities/validators/bankAccountNumber';
import { CurrencyInput } from 'views/components/CurrencyInput/CurrencyInput';
import { isValidCurrency } from 'tools/utilities/validators/currency';
import { isLowerCaseEqual } from 'tools/utilities/isLowerCaseEqual';
import { FuzzyLookup } from 'views/components/FuzzyLookup/FuzzyLookup';
import convertDate from 'tools/utilities/convertDate';
import formatDate from 'tools/utilities/formatDate';

interface IProps {
  intl: IntlShape;
  goToNextStep: (data: IPayroll) => void;
  goToPreviousStep: () => void;
  defaultValues?: IPayroll;
  bank: string;

  processingPayrollOptions: string[];
  payePaymentsFrequencyOptions: string[];
  leaveRolloverOptions: string[];

  isAccountant: boolean;
  accountantWillPaySmartPayrollFees: boolean;

  isCalcOnly: boolean;
  competingProducts: string[];
}

export const PayrollView = (props: IProps) => {
  const {
    intl,
    goToNextStep,
    goToPreviousStep,
    defaultValues,
    bank,
    processingPayrollOptions,
    payePaymentsFrequencyOptions,
    leaveRolloverOptions,
    isAccountant,
    accountantWillPaySmartPayrollFees,
    isCalcOnly,
    competingProducts,
  } = props;

  const [touchedNext, setTouchedNext] = useState(false);

  const [bankAccountName, setBankAccountName] = useState(defaultValues?.bankAccountName || '');
  const [bankAccountNumber, setBankAccountNumber] = useState(defaultValues?.bankAccountNumber || '');
  const [authoriseDirectDebitMonthlyFees, setAuthoriseDirectDebitMonthlyFees] = useState<boolean>(defaultValues?.authoriseDirectDebitMonthlyFees || false);
  const [notAuthorisedButRequestDirectDebitAuthority, setNotAuthorisedButRequestDirectDebitAuthority] = useState<boolean>(defaultValues?.notAuthorisedButRequestDirectDebitAuthority || false);
  const [processingPayroll, setProcessingPayroll] = useState(defaultValues?.processingPayroll || '');
  const [processingPayrollOther, setProcessingPayrollOther] = useState(defaultValues?.processingPayrollOther || '');
  const [processingPayrollOtherPayrollSoftware, setProcessingPayrollOtherPayrollSoftware] = useState(defaultValues?.otherProduct || '');
  const [payePaymentsToIrd, setPayePaymentsToIrd] = useState(defaultValues?.payePaymentsToIrd || '');
  const [employeeAnnualLeaveRollover, setEmployeeAnnualLeaveRollover] = useState(defaultValues?.employeeAnnualLeaveRollover || '');
  const [averageGrossAmountPerPayRun, setAverageGrossAmountPerPayRun] = useState<number>(defaultValues?.averageGrossAmountPerPayRun || 0);
  const [payrollProcessingStart, setPayrollProcessingStart] = useState<string>(defaultValues?.payrollProcessingStart || '');

  const bankAccountNameIsValid = !!bankAccountName && bankAccountName.length <= BANK_ACCOUNT_NAME_MAX_LENGTH;
  const bankAccountNumberIsValid = isValidBankAccountNumber(bankAccountNumber);

  const isProcessingPayrollOther = isLowerCaseEqual(processingPayroll, OPTION_OTHER);
  const isProcessingPayrollOtherPayrollSoftware = isLowerCaseEqual(processingPayroll, OPTION_OTHER_PAYROLL_SOFTWARE);
  const isProcessingPayrollOtherPayrollSoftwareOther = isLowerCaseEqual(processingPayrollOtherPayrollSoftware, OPTION_OTHER);

  const processingPayrollIsValid = !!processingPayroll;
  const processingPayrollOtherPayrollSoftwareIsValid = !isProcessingPayrollOtherPayrollSoftware || (isProcessingPayrollOtherPayrollSoftware && !!processingPayrollOtherPayrollSoftware);
  // If processingPayroll === "Other" then this is true because it's optional
  // If processingPayroll === "Other Payroll Software" && processingPayrollOtherPayrollSoftware === "Other" then it's required
  const processingPayrollOtherIsValid = !isProcessingPayrollOtherPayrollSoftwareOther || (isProcessingPayrollOtherPayrollSoftwareOther && !!processingPayrollOther);

  const payePaymentsToIrdIsValid = !!payePaymentsToIrd;
  const employeeAnnualLeaveRolloverIsValid = !!employeeAnnualLeaveRollover;
  const averageGrossAmountPerPayRunIsValid = (bank !== BANK_ASB) || (bank === BANK_ASB && isValidCurrency(averageGrossAmountPerPayRun));

  const isValid = bankAccountNameIsValid
    && bankAccountNumberIsValid
    && processingPayrollIsValid
    && processingPayrollOtherPayrollSoftwareIsValid
    && processingPayrollOtherIsValid
    && payePaymentsToIrdIsValid
    && employeeAnnualLeaveRolloverIsValid
    && averageGrossAmountPerPayRunIsValid
    && payrollProcessingStart;

  const today = new Date();
  const twoDays = new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000);

  const onNext = () => {
    setTouchedNext(true);

    if (!isValid) {
      scrollToError();
      return;
    }

    goToNextStep({
      bankAccountName,
      bankAccountNumber,
      authoriseDirectDebitMonthlyFees,
      notAuthorisedButRequestDirectDebitAuthority,
      processingPayroll,
      processingPayrollOther,
      otherProduct: processingPayrollOtherPayrollSoftware,
      payePaymentsToIrd,
      employeeAnnualLeaveRollover,
      averageGrossAmountPerPayRun,
      payrollProcessingStart: formatDate(payrollProcessingStart),
    });
  };

  const isAccountantAndWillPaySmartPayrollFees = isAccountant && accountantWillPaySmartPayrollFees;

  const showAuthorisePayrollCheckboxes = !isAccountantAndWillPaySmartPayrollFees;
  const showAuthoriseDirectDebitMonthlyFees = !isAccountant && !accountantWillPaySmartPayrollFees;

  return (
    <div className="Payroll">
      <FlowProgressHeader flowMatch={StepMatch.PayrollInformation} />

      <div className="Container">
        <div className="Grid">

          {/* Left */}
          <div className="Grid-cell u-width1of1 u-md-width1of2 u-lg-width7of12 u-xl-width2of3">
            <div className="Tile">
              <div className="Payroll-block">
                <FormattedMessage tagName="h2" id="PAYROLL_INFORMATION" />
                {!isCalcOnly && (
                  <FormattedMessage tagName="p" id="PAYROLL_TEXT" />
                )}

                <div className="">
                  {/* Bank is READONLY as this changes prior fields */}
                  <TextInput
                    className="TextInput--3of4"
                    isReadonly
                    id="bank"
                    value={bank}
                    onChange={() => {}}
                    type="text"
                    label={intl.formatMessage({ id: 'BANK' })}
                    placeholder=""
                  />
                  <FormattedMessage tagName="small" id="BANK_IS_READONLY" />
                </div>

                <TextInput
                  className="TextInput--3of4"
                  id="bankAccountName"
                  value={bankAccountName}
                  onChange={setBankAccountName}
                  type="text"
                  label={intl.formatMessage({ id: 'BANK_ACCOUNT_NAME' })}
                  placeholder=""
                  error={touchedNext && getErrorMessageFor(bankAccountNameIsValid, 'ERROR_BANK_ACCOUNT_NAME', intl)}
                  maxLength={BANK_ACCOUNT_NAME_MAX_LENGTH}
                />

                <MaskedInput
                  type="tel"
                  mask="99-9999-9999999-999"
                  className="TextInput--3of4"
                  id="bankAccountNumber"
                  value={bankAccountNumber}
                  onChange={setBankAccountNumber}
                  label={intl.formatMessage({ id: 'BANK_ACCOUNT_NUMBER' })}
                  placeholder={intl.formatMessage({ id: 'BANK_NUMBER_PLACEHOLDER' })}
                  error={touchedNext && getErrorMessageFor(bankAccountNumberIsValid, 'ERROR_BANK_ACCOUNT_NUMBER', intl)}
                />

                {showAuthorisePayrollCheckboxes && (
                  <fieldset className="Payroll-authorise">

                    <legend>
                      <FormattedMessage id="DIRECT_DEBIT" />
                    </legend>

                    <div className="Payroll-authorise-checkboxes">
                      {showAuthoriseDirectDebitMonthlyFees && (
                        <CheckboxInput
                          id="authoriseDirectDebitMonthlyFees"
                          value="authoriseDirectDebitMonthlyFees"
                          isChecked={authoriseDirectDebitMonthlyFees}
                          onChange={() => {
                            // Reset other checkbox if it's set to true
                            if (notAuthorisedButRequestDirectDebitAuthority) {
                              setNotAuthorisedButRequestDirectDebitAuthority(false);
                            }

                            setAuthoriseDirectDebitMonthlyFees(!authoriseDirectDebitMonthlyFees);
                          }}
                          label={intl.formatMessage({ id: 'AUTHORISE_SMART_PAYROLL_TO_DIRECT_DEBIT_FEES' })}
                        />
                      )}

                      <CheckboxInput
                        id="notAuthorisedButRequestDirectDebitAuthority"
                        value="notAuthorisedButRequestDirectDebitAuthority"
                        isChecked={notAuthorisedButRequestDirectDebitAuthority}
                        onChange={() => {
                          // Reset other checkbox if it's set to true
                          if (authoriseDirectDebitMonthlyFees) {
                            setAuthoriseDirectDebitMonthlyFees(false);
                          }
                          setNotAuthorisedButRequestDirectDebitAuthority(!notAuthorisedButRequestDirectDebitAuthority);
                        }}
                        label={intl.formatMessage({ id: 'NOT_AUTHORISED_TO_DIRECT_DEBIT_FEES' })}
                      />
                    </div>

                    <p className="Payroll-link">
                      <FormattedMessage id="READ" />
                      &nbsp;
                      <a href={DD_AUTHORITY_TERMS_AND_CONDITIONS_URL} target="_blank" rel="noopener noreferrer">
                        <FormattedMessage id="TERMS_AND_CONDITIONS" />
                      </a>
                    </p>

                    {/* Always show info as it's relevant to both checkboxes */}
                    <div className="Payroll-authorise-info">
                      <InfoBlock message={intl.formatMessage({ id: 'SMART_PAYROLL_DIRECT_DEBIT_DISCLAIMER' })} />
                    </div>
                  </fieldset>
                )}
              </div>

              <SelectInput
                id="processingPayroll"
                defaultValue={processingPayroll}
                onChange={(val) => {
                  // Reset the related fields whenever this is changed
                  setProcessingPayrollOther('');
                  setProcessingPayrollOtherPayrollSoftware('');

                  setProcessingPayroll(val);
                }}
                options={processingPayrollOptions}
                placeholder={intl.formatMessage({ id: 'SELECT' })}
                label={intl.formatMessage({ id: 'PROCESSING_PAYROLL' })}
                error={touchedNext && getErrorMessageFor(processingPayrollIsValid, 'ERROR_PROCESSING_PAYROLL', intl)}
              />

              {isLowerCaseEqual(processingPayroll, OPTION_OTHER_PAYROLL_SOFTWARE) && (
                <FuzzyLookup
                  id="processingOtherPayrollSoftware"
                  value={processingPayrollOtherPayrollSoftware}
                  onChange={setProcessingPayrollOtherPayrollSoftware}
                  items={competingProducts}
                  placeholder={intl.formatMessage({ id: 'SELECT' })}
                  label={intl.formatMessage({ id: 'PAYROLL_OTHER_PAYROLL_SOFTWARE' })}
                  errorMessage={touchedNext && getErrorMessageFor(processingPayrollOtherPayrollSoftwareIsValid, 'ERROR_PROCESSING_OTHER_PAYROLL_SOFTWARE', intl)}
                  clearLabel={intl.formatMessage({ id: 'BUTTON_CLEAR' })}
                  noResultsMessage={<FormattedMessage tagName="p" id="ERROR_PROCESSING_PAYROLL_NO_RESULTS" />}
                />
              )}

              {(isProcessingPayrollOther || isProcessingPayrollOtherPayrollSoftwareOther) && (
                <TextInput
                  className="TextInput--3of4"
                  id="processingPayrollOther"
                  value={processingPayrollOther}
                  onChange={setProcessingPayrollOther}
                  type="text"
                  label={intl.formatMessage({ id: isProcessingPayrollOther ? 'PLEASE_SPECIFY_OPTIONAL' : 'PLEASE_SPECIFY' })}
                  placeholder={intl.formatMessage({ id: 'ENTER_DETAILS_PLACEHOLDER' })}
                  error={touchedNext && getErrorMessageFor(processingPayrollOtherIsValid, 'ERROR_PROCESSING_OTHER_PAYROLL_SOFTWARE', intl)}
                />
              )}

              <SelectInput
                id="payePaymentsToIrd"
                defaultValue={payePaymentsToIrd}
                onChange={setPayePaymentsToIrd}
                options={payePaymentsFrequencyOptions}
                placeholder={intl.formatMessage({ id: 'SELECT' })}
                label={intl.formatMessage({ id: 'PAYE_PAYMENTS_TO_IRD' })}
                error={touchedNext && getErrorMessageFor(payePaymentsToIrdIsValid, 'ERROR_PAYE_PAYMENTS_TO_IRD', intl)}
              />

              <SelectInput
                id="employeeAnnualLeaveRollover"
                defaultValue={employeeAnnualLeaveRollover}
                onChange={setEmployeeAnnualLeaveRollover}
                options={leaveRolloverOptions}
                placeholder={intl.formatMessage({ id: 'SELECT' })}
                label={intl.formatMessage({ id: 'EMPLOYEE_LEAVE_ROLLOVER' })}
                error={touchedNext && getErrorMessageFor(employeeAnnualLeaveRolloverIsValid, 'ERROR_EMPLOYEE_LEAVE_ROLLOVER', intl)}
              />

              {/* Field conditional only if ASB is selected.  */}
              {bank === BANK_ASB && (
                <CurrencyInput
                  className="TextInput--3of4"
                  id="averageGrossAmountPerPayRun"
                  value={averageGrossAmountPerPayRun}
                  onChange={setAverageGrossAmountPerPayRun}
                  placeholder={intl.formatMessage({ id: 'ENTER_AMOUNT' })}
                  label={intl.formatMessage({ id: 'AVERAGE_GROSS_AMOUNT_PER_PAY_RUN' })}
                  error={touchedNext && getErrorMessageFor(averageGrossAmountPerPayRunIsValid, 'ERROR_AVERAGE_GROSS_AMOUNT_PER_PAY_RUN', intl)}
                  prefix="$"
                />
              )}

              <DateInput
                id="startProcessingPayrollOther"
                className="TextInput--3of4"
                value={payrollProcessingStart}
                min={convertDate(twoDays)}
                onChange={setPayrollProcessingStart}
                label={intl.formatMessage({ id: 'PAYROLL_PROCESSING_START' })}
                error={touchedNext && getErrorMessageFor(payrollProcessingStart !== '', 'ERROR_PROCESSING_START', intl)}
                placeholder={intl.formatMessage({ id: 'PAYROLL_PROCESSING_START_PLACEHOLDER' })}
              />

              <FlowButtonBar
                secondaryLabel={intl.formatMessage({ id: 'BUTTON_BACK' })}
                onPrimary={onNext}
                onSecondary={goToPreviousStep}
              />
            </div>
          </div>

          {/* Right */}
          <div className="Grid-cell u-width1of1 u-md-width1of2 u-lg-width5of12 u-xl-width1of3">
            <Pricing />
          </div>

        </div>
      </div>
    </div>
  );
};
